import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Footer } from '../components/Footer';
import { AnimatedHero } from '../components/AnimatedHero';
import { WaitlistModal } from '../components/WaitlistModal';
import { EditIcon, BookIcon, TargetIcon } from '../components/Icons';

/**
 * PreLaunchPage - Landing page with waitlist CTAs and platform screenshots
 *
 * Screenshots needed in /public directory:
 * ✓ writer-dashboard.png (exists)
 * ✓ reader-dashboard.png (exists)
 * ✓ agent-dashboard.png (exists)
 * ✗ discover-readers.png (placeholder - Reader directory/browse page)
 * ✗ agent-directory.png (placeholder - Agent directory page)
 * ✗ discover-manuscripts.png (placeholder - Manuscript discovery page)
 * ✗ beta-feedback.png (placeholder - Beta reader feedback page)
 * ✗ query-inbox.png (placeholder - Agent query inbox)
 * ✗ manuscript-review.png (placeholder - Manuscript reader/review page)
 */

export const PreLaunchPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const screenshots = [
    {
      title: 'Writer Dashboard',
      description: 'Keep your manuscripts, beta readers and next steps together in the workspace we’re building.',
      image: '/writer-dashboard.png',
      icon: <EditIcon />,
      category: 'For Writers',
    },
    {
      title: 'Manage Beta Reader Applications',
      description: 'Get to know interested readers, explore their favourite genres and choose who you’d like feedback from.',
      image: '/discover-readers.png',
      icon: <BookIcon />,
      category: 'For Writers',
    },
    {
      title: 'Find Literary Agents',
      description: 'We’re planning an agent directory with wish lists and links to each agent’s submission requirements.',
      image: '/agent-directory.png',
      icon: <TargetIcon />,
      category: 'For Writers',
    },
    {
      title: 'Beta Reader Dashboard',
      description: 'Find stories in your favourite genres and keep your reading and feedback organised.',
      image: '/reader-dashboard.png',
      icon: <BookIcon />,
      category: 'For Beta Readers',
    },
    {
      title: 'Discover Manuscripts',
      description: 'Explore manuscripts by genre and length, check content notes and find a story you’d like to read.',
      image: '/discover-manuscripts.png',
      icon: <BookIcon />,
      category: 'For Beta Readers',
    },
    {
      title: 'Provide Feedback',
      description: 'Share the moments that stayed with you, the questions you had and the passages that could be clearer.',
      image: '/beta-feedback.png',
      icon: <EditIcon />,
      category: 'For Beta Readers',
    },
    {
      title: 'Agent Dashboard',
      description: 'A proposed workspace for literary agents to organise queries and manuscript requests.',
      image: '/agent-dashboard.png',
      icon: <TargetIcon />,
      category: 'For Literary Agents',
    },
    {
      title: 'Query Inbox',
      description: 'We’re exploring ways for agents to organise query letters and follow up on manuscript requests.',
      image: '/query-inbox.png',
      icon: <TargetIcon />,
      category: 'For Literary Agents',
    },
    {
      title: 'Manuscript Review',
      description: 'A look at how manuscript reading and notes could sit alongside an agent’s submission workflow.',
      image: '/manuscript-review.png',
      icon: <BookIcon />,
      category: 'For Literary Agents',
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      overflowX: 'clip',
      backgroundColor: theme.colors.background,
      color: theme.colors.textPrimary,
      fontFamily: theme.fonts.primary,
    }}>
      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .prelaunch-header {
            padding: 16px 20px !important;
          }
          .prelaunch-logo {
            width: 32px !important;
            height: 32px !important;
          }
          .prelaunch-logo-text {
            font-size: 18px !important;
          }
          .prelaunch-header-btn {
            padding: 8px 16px !important;
            font-size: 14px !important;
          }
          .screenshots-section {
            padding: 48px 24px !important;
          }
          .screenshots-header {
            margin-bottom: 48px !important;
          }
          .screenshots-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .screenshot-card {
            display: flex !important;
            flex-direction: column !important;
            gap: 32px !important;
            grid-template-columns: 1fr !important;
          }
          .screenshot-text {
            order: 1 !important;
          }
          .screenshot-image {
            order: 2 !important;
          }
          .screenshot-icon {
            width: 48px !important;
            height: 48px !important;
            margin-bottom: 20px !important;
          }
          .section-badge {
            font-size: 11px !important;
            padding: 4px 10px !important;
          }
          .section-title {
            font-size: 32px !important;
            margin-bottom: 16px !important;
          }
          .section-subtitle {
            font-size: 16px !important;
            line-height: 1.5 !important;
          }
          .screenshot-title {
            font-size: 22px !important;
            margin-bottom: 12px !important;
          }
          .screenshot-description {
            font-size: 15px !important;
            line-height: 1.6 !important;
            margin-bottom: 24px !important;
          }
          .screenshot-button {
            padding: 12px 24px !important;
            font-size: 15px !important;
          }
          .screenshot-image-container {
            border-radius: 12px !important;
          }
          .benefits-section {
            padding: 48px 24px !important;
          }
          .benefits-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .cta-section {
            padding: 48px 24px !important;
          }
          .cta-title {
            font-size: 36px !important;
            margin-bottom: 20px !important;
          }
          .cta-description {
            font-size: 16px !important;
            line-height: 1.5 !important;
            margin-bottom: 32px !important;
          }
          .cta-button {
            padding: 14px 32px !important;
            font-size: 16px !important;
          }
        }
      `}</style>

      {/* Header */}
      <header className="prelaunch-header" style={{
        backgroundColor: theme.colors.white,
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: '20px 40px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <img
              className="prelaunch-logo"
              src="/ManuscriptMatchLogo.svg"
              alt="Manuscript Match Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div className="prelaunch-logo-text" style={{
              fontSize: '24px',
              fontWeight: '600',
              color: theme.colors.primary,
              letterSpacing: '-0.01em',
            }}>
              Manuscript Match
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: '20px',
            alignItems: 'center',
          }}>
            <a
              href="/blog"
              style={{
                color: theme.colors.textSecondary,
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                transition: 'color 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = theme.colors.primary;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = theme.colors.textSecondary;
              }}
            >
              Blog
            </a>
            <button
              className="prelaunch-header-btn"
              onClick={() => setIsModalOpen(true)}
              style={{
                backgroundColor: theme.colors.primary,
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: theme.borderRadius.medium,
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primaryDark;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
              }}
            >
              Join Waitlist
            </button>
          </div>
        </div>
      </header>

      <main id="main-content">
        {/* Hero Section */}
        <AnimatedHero />

        {/* See How It Works Section */}
        <section className="screenshots-section" style={{
          backgroundColor: theme.colors.backgroundAlt,
          padding: '100px 40px',
          borderTop: `1px solid ${theme.colors.border}`,
          borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
          }}>
            <div className="screenshots-header" style={{
              textAlign: 'center',
              marginBottom: '80px',
            }}>
              <div className="section-badge" style={{
                display: 'inline-block',
                backgroundColor: theme.colors.primaryLight,
                color: theme.colors.primaryDark,
                padding: '6px 14px',
                borderRadius: theme.borderRadius.pill,
                fontSize: '13px',
                fontWeight: '600',
                marginBottom: '20px',
                letterSpacing: '0.02em',
              }}>
                Platform Preview
              </div>
              <h2 className="section-title" style={{
                fontSize: '48px',
                fontWeight: '600',
                marginBottom: '20px',
                color: theme.colors.textPrimary,
                letterSpacing: '-0.02em',
              }}>
                See How It Works
              </h2>
              <p className="section-subtitle" style={{
                fontSize: '20px',
                lineHeight: '1.6',
                color: theme.colors.textSecondary,
                maxWidth: '700px',
                margin: '0 auto',
              }}>
                A preview of the features we’re developing, shown with example manuscripts and feedback.
              </p>
            </div>

            <div className="screenshots-grid" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '80px',
            }}>
              {screenshots.map((screenshot, index) => (
                <div
                  key={index}
                  className="screenshot-card"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: index % 2 === 0 ? '1fr 1.2fr' : '1.2fr 1fr',
                    gap: '60px',
                    alignItems: 'center',
                  }}
                >
                  {/* Text content */}
                  <div className="screenshot-text" style={{
                    order: index % 2 === 0 ? 1 : 2,
                  }}>
                    <div className="screenshot-icon" style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '56px',
                      height: '56px',
                      backgroundColor: theme.colors.primaryLight,
                      borderRadius: theme.borderRadius.large,
                      color: theme.colors.primary,
                      marginBottom: '24px',
                    }}>
                      {screenshot.icon}
                    </div>
                    <h3 className="screenshot-title" style={{
                      fontSize: '32px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      color: theme.colors.textPrimary,
                      letterSpacing: '-0.01em',
                    }}>
                      {screenshot.title}
                    </h3>
                    <p className="screenshot-description" style={{
                      fontSize: '18px',
                      lineHeight: '1.7',
                      color: theme.colors.textSecondary,
                      marginBottom: '32px',
                    }}>
                      {screenshot.description}
                    </p>
                    <button
                      className="screenshot-button"
                      onClick={() => setIsModalOpen(true)}
                      style={{
                        backgroundColor: 'transparent',
                        color: theme.colors.primary,
                        border: `2px solid ${theme.colors.primary}`,
                        padding: '12px 28px',
                        borderRadius: theme.borderRadius.medium,
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = theme.colors.primary;
                      }}
                    >
                      Get Early Access
                    </button>
                  </div>

                  {/* Screenshot */}
                  <div
                    className="screenshot-image"
                    style={{
                      order: index % 2 === 0 ? 2 : 1,
                      position: 'relative',
                    }}
                  >
                    <div className="screenshot-image-container" style={{
                      borderRadius: theme.borderRadius.xlarge,
                      overflow: 'hidden',
                      border: `2px solid ${theme.colors.border}`,
                      backgroundColor: theme.colors.white,
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                    }}>
                      <img
                        src={screenshot.image}
                        alt={screenshot.title}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits-section" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '100px 40px',
        }}>
          <h3 className="section-title" style={{
            fontSize: '48px',
            fontWeight: '600',
            textAlign: 'center',
            marginBottom: '20px',
            color: theme.colors.textPrimary,
            letterSpacing: '-0.02em',
          }}>
            For Everyone in the Literary Community
          </h3>
          <p className="section-subtitle" style={{
            fontSize: '20px',
            lineHeight: '1.6',
            textAlign: 'center',
            color: theme.colors.textSecondary,
            maxWidth: '700px',
            margin: '0 auto 80px',
          }}>
            We’re building Manuscript Match for writers, beta readers and literary agents.
          </p>

          <div className="benefits-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '40px',
          }}>
            {/* Writers */}
            <div style={{
              backgroundColor: theme.colors.white,
              borderRadius: theme.borderRadius.xlarge,
              padding: '40px',
              border: `1px solid ${theme.colors.border}`,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '56px',
                height: '56px',
                backgroundColor: theme.colors.primaryLight,
                borderRadius: theme.borderRadius.large,
                color: theme.colors.primary,
                marginBottom: '24px',
              }}>
                <EditIcon />
              </div>
              <h4 style={{
                fontSize: '24px',
                fontWeight: '600',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
                letterSpacing: '-0.01em',
              }}>
                For Writers
              </h4>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                fontSize: '16px',
                lineHeight: '2',
                color: theme.colors.textSecondary,
              }}>
                <li style={{ marginBottom: '8px' }}>✓ Find beta readers who love your genre</li>
                <li style={{ marginBottom: '8px' }}>✓ Get detailed, constructive feedback</li>
                <li style={{ marginBottom: '8px' }}>✓ Research literary agents</li>
                <li style={{ marginBottom: '8px' }}>✓ Track your query submissions</li>
                <li>✓ Build your writer network</li>
              </ul>
            </div>

            {/* Beta Readers */}
            <div style={{
              backgroundColor: theme.colors.white,
              borderRadius: theme.borderRadius.xlarge,
              padding: '40px',
              border: `1px solid ${theme.colors.border}`,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '56px',
                height: '56px',
                backgroundColor: theme.colors.primaryLight,
                borderRadius: theme.borderRadius.large,
                color: theme.colors.primary,
                marginBottom: '24px',
              }}>
                <BookIcon />
              </div>
              <h4 style={{
                fontSize: '24px',
                fontWeight: '600',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
                letterSpacing: '-0.01em',
              }}>
                For Beta Readers
              </h4>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                fontSize: '16px',
                lineHeight: '2',
                color: theme.colors.textSecondary,
              }}>
                <li style={{ marginBottom: '8px' }}>✓ Discover manuscripts before publication</li>
                <li style={{ marginBottom: '8px' }}>✓ Read in your favorite genres</li>
                <li style={{ marginBottom: '8px' }}>✓ Help shape great stories</li>
                <li style={{ marginBottom: '8px' }}>✓ Connect directly with authors</li>
                <li>✓ Build your reading portfolio</li>
              </ul>
            </div>

            {/* Agents */}
            <div style={{
              backgroundColor: theme.colors.white,
              borderRadius: theme.borderRadius.xlarge,
              padding: '40px',
              border: `1px solid ${theme.colors.border}`,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '56px',
                height: '56px',
                backgroundColor: theme.colors.primaryLight,
                borderRadius: theme.borderRadius.large,
                color: theme.colors.primary,
                marginBottom: '24px',
              }}>
                <TargetIcon />
              </div>
              <h4 style={{
                fontSize: '24px',
                fontWeight: '600',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
                letterSpacing: '-0.01em',
              }}>
                For Literary Agents
              </h4>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                fontSize: '16px',
                lineHeight: '2',
                color: theme.colors.textSecondary,
              }}>
                <li style={{ marginBottom: '8px' }}>✓ Share your submission interests</li>
                <li style={{ marginBottom: '8px' }}>✓ Manage your query inbox efficiently</li>
                <li style={{ marginBottom: '8px' }}>✓ Share your MSWL with writers</li>
                <li style={{ marginBottom: '8px' }}>✓ Track manuscript requests</li>
                <li>✓ Discover new writing</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="cta-section" style={{
          backgroundColor: theme.colors.backgroundAlt,
          padding: '100px 40px',
          borderTop: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center',
          }}>
            <div className="section-badge" style={{
              display: 'inline-block',
              backgroundColor: theme.colors.primaryLight,
              color: theme.colors.primaryDark,
              padding: '6px 14px',
              borderRadius: theme.borderRadius.pill,
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '24px',
              letterSpacing: '0.02em',
            }}>
              Join the Waitlist
            </div>
            <h2 className="cta-title" style={{
              fontSize: '56px',
              fontWeight: '600',
              marginBottom: '24px',
              color: theme.colors.textPrimary,
              letterSpacing: '-0.02em',
              lineHeight: '1.1',
            }}>
              Ready to Join?
            </h2>
            <p className="cta-description" style={{
              fontSize: '20px',
              lineHeight: '1.6',
              color: theme.colors.textSecondary,
              marginBottom: '40px',
              maxWidth: '600px',
              margin: '0 auto 40px',
            }}>
              Join the waitlist and we’ll email you when Manuscript Match is ready. You don’t need a finished manuscript to join.
            </p>
            <button
              className="cta-button"
              onClick={() => setIsModalOpen(true)}
              style={{
                backgroundColor: theme.colors.primary,
                color: 'white',
                border: 'none',
                padding: '16px 40px',
                borderRadius: theme.borderRadius.medium,
                fontSize: '18px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primaryDark;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(99, 102, 241, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.2)';
              }}
            >
              Join the Waitlist
            </button>
          </div>
        </section>
      </main>

      <Footer />

      <WaitlistModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};
